import React, { useRef, useState } from 'react';
import { LocalizationProvider, MobileDateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ruLocale from 'date-fns/locale/ru';
import kkLocale from "date-fns/locale/kk";
import { 
  Autocomplete,
  Box, 
  Button, 
  Checkbox,
  Chip,
  Divider, 
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid, 
  Paper, 
  Tab,
  Tabs,
  TextField, 
  TextFieldProps, 
  Typography
} from "@mui/material";
import ThumbnailChooser from "./ThumbnailChooser";
import { Field, FieldProps, Formik, FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { State } from "../../../redux/store";
import { formValidation } from "../../../utils/formValidation";
import { useSnack } from "../../../utils/useSnack";
import { MDXEditorForm, ValidationSchema, initialValues } from "./formik";
import MDXEditor from '../../../common/MDXEditor/MDXEditor';
import '@mdxeditor/editor/style.css';

interface MDXEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Компонент для отображения предпросмотра изображения
const ThumbnailPreview = ({ imageUrl, tags, t }: { imageUrl: string; tags: string[]; t: (key: string) => string }) => {
  return (
    <Paper 
      elevation={3} 
      sx={{ 
        mt: 3, 
        p: 2, 
        borderRadius: 2,
        bgcolor: '#f8f9fa'
      }}
    >
      <Typography variant="h6" gutterBottom>
        {t("cabinet.mdx_editor.content_preview")}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Box 
        sx={{ 
          p: 2, 
          bgcolor: '#fff',
          border: '1px solid #e0e0e0', 
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Box 
          sx={{ 
            border: '1px solid #eeeeee',
            p: 1,
            borderRadius: 1,
            width: '100%',
            mb: 2
          }}
        >
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t("cabinet.mdx_editor.title_placeholder")}
          </Typography>
        </Box>

        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            width: '100%',
            gap: 2,
            alignItems: { xs: 'center', sm: 'flex-start' }
          }}
        >
          {/* Изображение */}
          <Box 
            sx={{ 
              width: { xs: '100%', sm: '40%' },
              maxWidth: '300px',
              mb: { xs: 2, sm: 0 }
            }}
          >
            <img 
              src={imageUrl} 
              alt="Предпросмотр" 
              style={{ 
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                objectFit: 'cover',
                aspectRatio: '16/9'
              }}
              onError={(e) => {
                console.error('Ошибка загрузки изображения для предпросмотра:', imageUrl);
                (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJmZWF0aGVyIGZlYXRoZXItaW1hZ2UiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDEzLjUgMTUgMTAgMjAiPjwvcG9seWxpbmU+PC9zdmc+';  
              }}
            />
          </Box>

          {/* Текст */}
          <Box sx={{ width: { xs: '100%', sm: '60%' } }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {t("cabinet.mdx_editor.excerpt_placeholder")}
            </Typography>

            <Box sx={{ mt: 2 }}>
              {tags.map((tag, index) => (
                <Chip 
                  key={index} 
                  label={tag} 
                  size="small" 
                  sx={{ mr: 0.5, mb: 0.5 }}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
        {t("cabinet.mdx_editor.preview_note")}
      </Typography>
    </Paper>
  );
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`language-tabpanel-${index}`}
      aria-labelledby={`language-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `language-tab-${index}`,
    'aria-controls': `language-tabpanel-${index}`,
  };
}

export default function MDXEditorComponent() {
  const { t, i18n } = useTranslation();
  const { snack } = useSnack();
  const navigate = useNavigate();
  const state = useSelector((state: State) => state);
  const currentTime: string | null = state.public?.currentTime;
  const currentDate = currentTime ? new Date(currentTime) : new Date();
  const formikRef = useRef<FormikProps<MDXEditorForm>>(null);

  // Добавляем состояние для отслеживания обновлений превью
  const [thumbnailPreview, setThumbnailPreview] = useState<{id: string | number | null, url: string} | null>(null);

  // Tab state for each multilingual field
  const [titleTabValue, setTitleTabValue] = useState(0);
  const [contentTabValue, setContentTabValue] = useState(0);
  const [excerptTabValue, setExcerptTabValue] = useState(0);

  const handleTitleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTitleTabValue(newValue);
  };

  const handleContentTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setContentTabValue(newValue);
  };

  const handleExcerptTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setExcerptTabValue(newValue);
  };

  // Убедимся, что поле thumbnail правильно инициализировано
  React.useEffect(() => {
    if (formikRef.current && formikRef.current.values.thumbnail === undefined) {
      console.log('Инициализация поля thumbnail');
      formikRef.current.setFieldValue('thumbnail', { id: null, url: '' });
    }
  }, []);

  function onClose() {
    navigate("/admin-content");
  }

  async function submit() {
    await formValidation(formikRef);

    if (formikRef.current && formikRef.current.isValid) {
      formikRef.current.submitForm();
    } else {
      snack(t("snack.warning.required_fields"), "warning");
    }
  }

  async function onSubmit(values: MDXEditorForm) {
    console.log('Form values:', values);

    // Here you would typically send the data to the server
    // For now, we'll just show a success message
    snack(t("snack.success.content_created"), "success");
    navigate("/admin-content");
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={i18n.language === "kk" ? kkLocale : ruLocale}>
      <Paper sx={{ pl: 4, pr: 4, pt: 2, pb: 4 }} elevation={10}>
        <Grid container spacing={3} justifyContent="space-between">
          <Grid item>
            <Typography component="h5" variant="h5" sx={{ fontWeight: 700 }}>
              {t("cabinet.mdx_editor.title")}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ mt: 2, mb: 6, width: "100%" }} />

        <Formik
          innerRef={formikRef}
          initialValues={{...initialValues, thumbnail: {id: null, url: ''}}}
          validationSchema={ValidationSchema(currentDate)}
          onSubmit={onSubmit}
        >
          {(formikProps: FormikProps<MDXEditorForm>) => (
            <Grid container spacing={3}>
              {/* Language Selection */}
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">{t("cabinet.mdx_editor.available_languages")}</FormLabel>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formikProps.values.availableLanguages.includes('en')}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const availableLanguages = [...formikProps.values.availableLanguages];
                            if (checked && !availableLanguages.includes('en')) {
                              availableLanguages.push('en');
                            } else if (!checked) {
                              const index = availableLanguages.indexOf('en');
                              if (index !== -1) {
                                availableLanguages.splice(index, 1);
                              }
                            }
                            formikProps.setFieldValue('availableLanguages', availableLanguages);
                          }}
                        />
                      }
                      label="EN"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formikProps.values.availableLanguages.includes('ru')}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const availableLanguages = [...formikProps.values.availableLanguages];
                            if (checked && !availableLanguages.includes('ru')) {
                              availableLanguages.push('ru');
                            } else if (!checked) {
                              const index = availableLanguages.indexOf('ru');
                              if (index !== -1) {
                                availableLanguages.splice(index, 1);
                              }
                            }
                            formikProps.setFieldValue('availableLanguages', availableLanguages);
                          }}
                        />
                      }
                      label="RU"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formikProps.values.availableLanguages.includes('kk')}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const availableLanguages = [...formikProps.values.availableLanguages];
                            if (checked && !availableLanguages.includes('kk')) {
                              availableLanguages.push('kk');
                            } else if (!checked) {
                              const index = availableLanguages.indexOf('kk');
                              if (index !== -1) {
                                availableLanguages.splice(index, 1);
                              }
                            }
                            formikProps.setFieldValue('availableLanguages', availableLanguages);
                          }}
                        />
                      }
                      label="KK"
                    />
                  </FormGroup>
                </FormControl>
              </Grid>

              {/* Default Language */}
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <Field name="defaultLanguage">
                    {({ field, meta }: FieldProps) => (
                      <TextField
                        select
                        {...field}
                        label={t("cabinet.mdx_editor.default_language")}
                        error={!!(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error}
                        SelectProps={{
                          native: true,
                        }}
                        size="small"
                      >
                        {formikProps.values.availableLanguages.map((lang) => (
                          <option key={lang} value={lang}>
                            {lang.toUpperCase()}
                          </option>
                        ))}
                      </TextField>
                    )}
                  </Field>
                </FormControl>
              </Grid>

              {/* Published Time */}
              <Grid item xs={12} sm={6} md={4}>
                <MobileDateTimePicker
                  disabled={!currentDate}
                  minDateTime={currentDate ? new Date(currentDate) : new Date()}
                  value={formikProps.values.publishedTime}
                  onChange={(newValue: any) => formikProps.setFieldValue("publishedTime", newValue)}
                  inputFormat="dd/MM/yyyy HH:mm"
                  label={t("auction.dates.choose_date_and_time")}
                  renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => (
                    <Field name={"publishedTime"}>
                      {({ field, meta }: FieldProps) => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          color="primary"
                          label={t("cabinet.mdx_editor.published_time")}
                          error={!!(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error}
                          InputLabelProps={{ shrink: field.value ? true : false }}
                        />
                      )}
                    </Field>
                  )}
                />
              </Grid>

              {/* Title with language tabs */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  {t("cabinet.mdx_editor.title_field")}
                </Typography>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={titleTabValue} onChange={handleTitleTabChange} aria-label="title language tabs">
                    <Tab label="EN" {...a11yProps(0)} disabled={!formikProps.values.availableLanguages.includes('en')} />
                    <Tab label="RU" {...a11yProps(1)} disabled={!formikProps.values.availableLanguages.includes('ru')} />
                    <Tab label="KK" {...a11yProps(2)} disabled={!formikProps.values.availableLanguages.includes('kk')} />
                  </Tabs>
                </Box>
                <TabPanel value={titleTabValue} index={0}>
                  <Field name="title.en">
                    {({ field, meta }: FieldProps) => (
                      <TextField
                        {...field}
                        fullWidth
                        color="primary"
                        size="small"
                        label={t("cabinet.mdx_editor.title_en")}
                        error={!!(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error}
                        disabled={!formikProps.values.availableLanguages.includes('en')}
                      />
                    )}
                  </Field>
                </TabPanel>
                <TabPanel value={titleTabValue} index={1}>
                  <Field name="title.ru">
                    {({ field, meta }: FieldProps) => (
                      <TextField
                        {...field}
                        fullWidth
                        color="primary"
                        size="small"
                        label={t("cabinet.mdx_editor.title_ru")}
                        error={!!(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error}
                        disabled={!formikProps.values.availableLanguages.includes('ru')}
                      />
                    )}
                  </Field>
                </TabPanel>
                <TabPanel value={titleTabValue} index={2}>
                  <Field name="title.kk">
                    {({ field, meta }: FieldProps) => (
                      <TextField
                        {...field}
                        fullWidth
                        color="primary"
                        size="small"
                        label={t("cabinet.mdx_editor.title_kk")}
                        error={!!(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error}
                        disabled={!formikProps.values.availableLanguages.includes('kk')}
                      />
                    )}
                  </Field>
                </TabPanel>
              </Grid>

              {/* Content with language tabs */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  {t("cabinet.mdx_editor.content_field")}
                </Typography>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={contentTabValue} onChange={handleContentTabChange} aria-label="content language tabs">
                    <Tab label="EN" {...a11yProps(0)} disabled={!formikProps.values.availableLanguages.includes('en')} />
                    <Tab label="RU" {...a11yProps(1)} disabled={!formikProps.values.availableLanguages.includes('ru')} />
                    <Tab label="KK" {...a11yProps(2)} disabled={!formikProps.values.availableLanguages.includes('kk')} />
                  </Tabs>
                </Box>
                <TabPanel value={contentTabValue} index={0}>
                  <Field name="content.en">
                    {({ field, form, meta }: FieldProps) => (
                      <Box>
                        <Box>
                          <MDXEditor
                            value={field.value}
                            onChange={(value) => form.setFieldValue('content.en', value)}
                            contentEditableClassName="prose"
                            readOnly={!formikProps.values.availableLanguages.includes('en')}
                          />
                        </Box>
                        {meta.touched && meta.error && (
                          <Typography color="error" variant="caption">
                            {meta.error}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Field>
                </TabPanel>
                <TabPanel value={contentTabValue} index={1}>
                  <Field name="content.ru">
                    {({ field, form, meta }: FieldProps) => (
                      <Box>
                        <Box>
                          <MDXEditor
                            value={field.value}
                            onChange={(value) => form.setFieldValue('content.ru', value)}
                            contentEditableClassName="prose"
                            readOnly={!formikProps.values.availableLanguages.includes('ru')}
                          />
                        </Box>
                        {meta.touched && meta.error && (
                          <Typography color="error" variant="caption">
                            {meta.error}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Field>
                </TabPanel>
                <TabPanel value={contentTabValue} index={2}>
                  <Field name="content.kk">
                    {({ field, form, meta }: FieldProps) => (
                      <Box>
                        <Box>
                          <MDXEditor
                            value={field.value}
                            onChange={(value) => form.setFieldValue('content.kk', value)}
                            contentEditableClassName="prose"
                            readOnly={!formikProps.values.availableLanguages.includes('kk')}
                          />
                        </Box>
                        {meta.touched && meta.error && (
                          <Typography color="error" variant="caption">
                            {meta.error}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Field>
                </TabPanel>
              </Grid>

              {/* Excerpt with language tabs */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  {t("cabinet.mdx_editor.excerpt_field")}
                </Typography>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={excerptTabValue} onChange={handleExcerptTabChange} aria-label="excerpt language tabs">
                    <Tab label="EN" {...a11yProps(0)} disabled={!formikProps.values.availableLanguages.includes('en')} />
                    <Tab label="RU" {...a11yProps(1)} disabled={!formikProps.values.availableLanguages.includes('ru')} />
                    <Tab label="KK" {...a11yProps(2)} disabled={!formikProps.values.availableLanguages.includes('kk')} />
                  </Tabs>
                </Box>
                <TabPanel value={excerptTabValue} index={0}>
                  <Field name="excerpt.en">
                    {({ field, meta }: FieldProps) => (
                      <TextField
                        {...field}
                        multiline
                        rows={4}
                        fullWidth
                        color="primary"
                        size="small"
                        label={t("cabinet.mdx_editor.excerpt_en")}
                        error={!!(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error}
                        disabled={!formikProps.values.availableLanguages.includes('en')}
                      />
                    )}
                  </Field>
                </TabPanel>
                <TabPanel value={excerptTabValue} index={1}>
                  <Field name="excerpt.ru">
                    {({ field, meta }: FieldProps) => (
                      <TextField
                        {...field}
                        multiline
                        rows={4}
                        fullWidth
                        color="primary"
                        size="small"
                        label={t("cabinet.mdx_editor.excerpt_ru")}
                        error={!!(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error}
                        disabled={!formikProps.values.availableLanguages.includes('ru')}
                      />
                    )}
                  </Field>
                </TabPanel>
                <TabPanel value={excerptTabValue} index={2}>
                  <Field name="excerpt.kk">
                    {({ field, meta }: FieldProps) => (
                      <TextField
                        {...field}
                        multiline
                        rows={4}
                        fullWidth
                        color="primary"
                        size="small"
                        label={t("cabinet.mdx_editor.excerpt_kk")}
                        error={!!(meta.touched && meta.error)}
                        helperText={meta.touched && meta.error}
                        disabled={!formikProps.values.availableLanguages.includes('kk')}
                      />
                    )}
                  </Field>
                </TabPanel>
              </Grid>

              {/* Tags field */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  {t("cabinet.mdx_editor.tags_field")}
                </Typography>
                <Field name="tags">
                  {({ field, form, meta }: FieldProps) => {
                    // Predefined tag suggestions
                    const tagSuggestions = ["sport", "music", "movies", "technology", "business", "health", "education", "travel", "food", "fashion"];

                    return (
                      <Autocomplete
                        multiple
                        id="tags"
                        options={tagSuggestions}
                        freeSolo
                        value={field.value}
                        onChange={(_, newValue) => {
                          form.setFieldValue('tags', newValue);
                        }}
                        renderTags={(value: string[], getTagProps) =>
                          value.map((option: string, index: number) => (
                            <Chip
                              variant="outlined"
                              label={option}
                              {...getTagProps({ index })}
                              key={index}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            variant="outlined"
                            size="small"
                            placeholder={t("cabinet.mdx_editor.tags_placeholder")}
                            error={!!(meta.touched && meta.error)}
                            helperText={meta.touched && meta.error}
                          />
                        )}
                      />
                    );
                  }}
                </Field>
              </Grid>

              {/* Thumbnail field */}
              <Grid item xs={12}>
                <Field name="thumbnail">
                  {({ field, form, meta }: FieldProps) => {
                    console.log('Текущее значение thumbnail:', field.value);

                    // Проверка наличия необходимых значений
                    const thumbnailValue = field.value && typeof field.value === 'object' ? 
                      field.value : 
                      { id: null, url: '' };

                    // Используем значение из формы вместо локального состояния
                    const imagePreview = thumbnailValue.url || null;
                    console.log('Значение imagePreview:', imagePreview);

                    return (
                      <Box>
                        <ThumbnailChooser
                          value={thumbnailValue}
                          onChange={(image) => {
                            console.log('Обновление thumbnail в Formik:', image);

                            // Создаем новый объект для избежания проблем с ссылками
                            const newImage = {
                              id: image.id,
                              url: image.url
                            };

                            // Обновляем значение в формике
                            form.setFieldValue('thumbnail', newImage);

                            // Принудительное обновление Formik
                            form.setFieldTouched('thumbnail', true, false);

                            // Обновляем родительский компонент принудительно
                            formikProps.setFieldValue('thumbnail', newImage);

                            // Обновляем локальное состояние для перерисовки
                            setThumbnailPreview(newImage);

                            // Проверка успешного обновления
                            setTimeout(() => {
                              console.log('Проверка обновления thumbnail:', form.values.thumbnail);
                              console.log('Проверка в родительском formikProps:', formikProps.values.thumbnail);
                              console.log('Локальное состояние thumbnailPreview:', thumbnailPreview);
                            }, 100);
                          }}
                        />

                        {/* Предпросмотр изображения в карточке */}
                        {(imagePreview || (thumbnailPreview && thumbnailPreview.url)) ? (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {t("cabinet.mdx_editor.preview_available") || "Предпросмотр"}
                            </Typography>
                            <ThumbnailPreview 
                              imageUrl={thumbnailPreview?.url || imagePreview} 
                              tags={formikProps.values.tags} 
                              t={t} 
                            />
                          </Box>
                        ) : null}
                      </Box>
                    );
                  }}
                </Field>
              </Grid>
            </Grid>
          )}
        </Formik>

        <Divider sx={{ mt: 6, mb: 2, width: "100%" }} />

        <Grid item container spacing={2} justifyContent="center" sx={{ pt: 2 }}>
          <Grid item xs="auto">
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={onClose}
              color="secondary"
            >
              {t("cabinet.mdx_editor.cancel")}
            </Button>
          </Grid>
          <Grid item xs="auto">
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={submit}
              size="large"
            >
              {t("cabinet.mdx_editor.save")}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </LocalizationProvider>
  );
}
